import { inject } from '@angular/core';
import { AppFeatureActions } from '@app/app.actions';
import { getLanguage, getSelectedExhibitionId, getSelfRegistration } from '@app/app.reducer';
import { SetFormFields } from '@core/data-access/models/form/form-store.model';
import { FormName } from '@core/data-access/models/form/form.model';
import { DynamicFieldOptionsTranslationFactory } from '@core/data-access/services/dynamic-form-field/dynamic-field-options-translation-factory.service';
import { BuyerFormsSyncActions, FormFieldSyncActions } from '@core/data-access/store/form/form.actions';
import { RouterActions } from '@core/data-access/store/router/router.actions';
import { SavedTicketHoldersApi } from '@core/features/account/features/saved-ticket-holders/services/saved-ticket-holders-api';
import {
  CreateTicketHolderFormActions,
  SavedTicketHolderActions,
  SavedTicketHolderFormActions
} from '@core/features/account/features/saved-ticket-holders/store/saved-ticket-holder.actions';
import { selectSavedBuyerEmailAddress } from '@core/features/account/features/saved-ticket-holders/store/saved-ticket-holder.selectors';
import {
  mapCreatedTicketHolderResponseToSetSavedTicketHolders,
  mapGetSavedTicketHoldersResponseToSetSavedTicketHolders,
  mapUpdateTicketHolderMergedValuesToFormFields
} from '@core/features/account/features/saved-ticket-holders/utils/saved-ticket-holder-mappers';
import { selectBuyerQuestionnaireFormFields } from '@core/features/buyer-questionnaire/store/buyer-questionnaire.selectors';
import { TicketHolderActions } from '@core/features/ticket-assignment/store/ticket-assignment.actions';
import { setDisabledFormFields, setIsSavedFormFields } from '@core/utils/form-fields/form-field-helpers';
import { removeBuyerInformationFormFields } from '@features/buyer-information/utils/remove-buyer-information-form-fields';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { head, isEmpty, isNil } from 'lodash';
import { catchError, debounceTime, filter, map, of, switchMap } from 'rxjs';

export const setSavedTicketHolder$ = createEffect(
  (
    actions$ = inject(Actions),
    translateService = inject(TranslateService),
    dynamicFieldOptionsTranslationFactory = inject(DynamicFieldOptionsTranslationFactory),
    savedTicketHoldersApi = inject(SavedTicketHoldersApi)
  ) =>
    actions$.pipe(
      ofType(AppFeatureActions.savedTicketHolderFeatureAdded, RouterActions.navigatedToProfileRoute),
      debounceTime(1000),
      switchMap(() =>
        savedTicketHoldersApi.getSavedTicketHolders$().pipe(
          map((response) => {
            const setSavedTicketHolder = mapGetSavedTicketHoldersResponseToSetSavedTicketHolders(
              response,
              translateService,
              dynamicFieldOptionsTranslationFactory
            );

            return SavedTicketHolderActions.setSavedTicketHolders(setSavedTicketHolder);
          }),
          catchError(() => of(SavedTicketHolderActions.setSavedTicketHolders({})))
        )
      )
    ),
  { functional: true }
);

export const addCreatedSavedTicketHolderBeforeSaveOrder$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(SavedTicketHolderActions.setCreatedSavedTicketHolderPersonsBeforeSaveOrder),
      map(({ createdTicketHolders }) =>
        SavedTicketHolderActions.addCreatedTicketHoldersBeforeSaveOrder(createdTicketHolders)
      )
    ),
  { functional: true }
);

export const createTicketHolders$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    translateService = inject(TranslateService),
    dynamicFieldOptionsTranslationFactory = inject(DynamicFieldOptionsTranslationFactory),
    savedTicketHoldersApi = inject(SavedTicketHoldersApi)
  ) =>
    actions$.pipe(
      ofType(CreateTicketHolderFormActions.createTicketHolder),
      filter(({ createSavedTicketHoldersRequest }) => !isEmpty(createSavedTicketHoldersRequest)),
      concatLatestFrom(() => [
        store.select(getLanguage),
        store.select(selectSavedBuyerEmailAddress),
        store.select(getSelectedExhibitionId),
        store.select(getSelfRegistration)
      ]),
      switchMap(([{ createSavedTicketHoldersRequest }, language, buyerEmail, eventId, isSelfRegistration]) =>
        savedTicketHoldersApi
          .createSavedTicketHolders$(language, buyerEmail, eventId, isSelfRegistration, createSavedTicketHoldersRequest)
          .pipe(
            map((response) => {
              if (!isEmpty(head(response)?.validationError)) {
                return SavedTicketHolderActions.addCreatedTicketHolders({});
              }

              const setSavedTicketHolderForms = mapCreatedTicketHolderResponseToSetSavedTicketHolders(
                response,
                translateService,
                dynamicFieldOptionsTranslationFactory
              );

              return SavedTicketHolderActions.addCreatedTicketHolders(setSavedTicketHolderForms);
            }),
            catchError(() => of(SavedTicketHolderActions.addCreatedTicketHolders({})))
          )
      )
    ),
  { functional: true }
);

export const updateSavedTicketHolder$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), savedTicketHoldersApi = inject(SavedTicketHoldersApi)) =>
    actions$.pipe(
      ofType(SavedTicketHolderFormActions.updateSavedTicketHolder),
      filter(
        ({ person, formValues, savedAndEditedFormValues }) =>
          !isNil(person) && !isEmpty(formValues) && !isEmpty(savedAndEditedFormValues)
      ),
      concatLatestFrom(() => [
        store.select(getLanguage),
        store.select(getSelectedExhibitionId),
        store.select(getSelfRegistration)
      ]),
      switchMap(([{ person, formValues, savedAndEditedFormValues }, language, eventId, isSelfRegistration]) =>
        savedTicketHoldersApi
          .updateSavedTicketHolder$(language, eventId, isSelfRegistration, person.id, savedAndEditedFormValues)
          .pipe(
            map(() => {
              const updatedFormFields = mapUpdateTicketHolderMergedValuesToFormFields(formValues);

              return SavedTicketHolderActions.setUpdatedTicketHolder({ person, formFields: updatedFormFields });
            }),
            catchError(() => of(SavedTicketHolderActions.setUpdatedTicketHolder({ person: null, formFields: null })))
          )
      )
    ),
  { functional: true }
);

export const syncUpdatedTicketHolderForms$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(SavedTicketHolderActions.setUpdatedTicketHolder),
      filter(({ person, formFields }) => !isNil(person) && !isNil(formFields)),
      map(({ person, formFields }) => {
        const setFormFields: SetFormFields = {
          person,
          originForm: FormName.SavedTicketHolders,
          syncForms: [FormName.BuyerInformation, FormName.TicketHolder],
          formFields: setDisabledFormFields(person, formFields)
        };

        return FormFieldSyncActions.setFormFields(setFormFields);
      })
    ),
  {
    functional: true
  }
);

export const deleteSavedTicketHolder$ = createEffect(
  (actions$ = inject(Actions), savedTicketHoldersApi = inject(SavedTicketHoldersApi)) =>
    actions$.pipe(
      ofType(SavedTicketHolderFormActions.deleteSavedTicketHolder),
      filter(({ person }) => !isNil(person)),
      switchMap(({ person }) =>
        savedTicketHoldersApi.deleteSavedTicketHolder$(person.id).pipe(
          map(() => SavedTicketHolderActions.removeDeletedTicketHolder(person)),
          catchError(() => of(SavedTicketHolderActions.removeDeletedTicketHolder(null)))
        )
      )
    ),
  { functional: true }
);

export const unassignDeletedTicketHolder$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(SavedTicketHolderActions.removeDeletedTicketHolder),
      filter(({ person }) => !isNil(person)),
      map(({ person }) => TicketHolderActions.unassignDeletedTicketHolder({ person }))
    ),
  {
    functional: true
  }
);

export const setSavedTicketHolderQuestionnaireFormFieldsWithoutSavingAfterBuyerFormPrefill$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(BuyerFormsSyncActions.setBuyerInformationFormPrefill),
      concatLatestFrom(() => store.select(selectBuyerQuestionnaireFormFields)),
      filter(([_, questionnaireFormFields]) => !isEmpty(questionnaireFormFields)),
      map(([{ person }, formFields]) =>
        SavedTicketHolderFormActions.setSavedTicketHolderQuestionnaireFormFieldsWithoutSaving({
          person,
          formFields: setIsSavedFormFields(formFields, false)
        })
      )
    ),
  { functional: true }
);

export const setSavedTicketHolderFormFieldsWithoutSaving$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(FormFieldSyncActions.setTicketHolderFormFields),
      filter(({ person }) => !isNil(person.id)),
      map(({ person, formFields }) => ({ person, formFields: removeBuyerInformationFormFields(formFields) })),
      filter(({ formFields }) => !isEmpty(formFields)),
      map(({ person, formFields }) =>
        SavedTicketHolderFormActions.setSavedTicketHolderFormFieldsWithoutSaving({
          person,
          formFields: setIsSavedFormFields(formFields, false)
        })
      )
    ),
  { functional: true }
);

export const setSavedTicketHolderQuestionnaireFormFieldsWithoutSaving$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(FormFieldSyncActions.setTicketHolderQuestionnaireFormFields),
      filter(({ person }) => !isNil(person.id)),
      map(({ person, formFields }) =>
        SavedTicketHolderFormActions.setSavedTicketHolderQuestionnaireFormFieldsWithoutSaving({
          person,
          formFields: setIsSavedFormFields(formFields, false)
        })
      )
    ),
  { functional: true }
);
